import { Tab } from '@headlessui/react'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import NutritionFacts from './nutrition-facts'
import ResourcesDownload from './resources-downloads'
import Image from 'next/image'
import { FROZEN, PRODUCT_TABS, REFRIGERATED } from '@constants/index'

function classNames(...classes: any) {
  return classes.filter(Boolean).join(' ')
}

export default function ProductDetailsTab({ product }: any) {
  const { t: translate } = useTranslation(['common', 'pdp'])

  const ProductTabData = product?.mag_attributes?.find(
    (attr: { attribute_code: string }) =>
      attr.attribute_code === 'product_info',
  )?.value?.[0]
  let [tabHeading] = useState({
    'text-product-about-this-product': '',
    'text-product-specifications': '',
    'text-product-properties': '',
    'text-product-allergens': '',
    'text-product-nutrition-facts': '',
    'text-product-resources-and-downloads': '',
  })

  const shouldShowFrozen =
    product.temperature_description === FROZEN ||
    product.temperature_description === REFRIGERATED

  const product_mag: any = {}
  const product_oms: any = {}
  const product_onews: any = {}
  const product_pim: any = {}
  for (const key in product) {
    if (key.startsWith('mag')) {
      product_mag[key] = product[key]
    } else if (key.startsWith('oms')) {
      product_oms[key] = product[key]
    } else if (key.startsWith('onews')) {
      product_onews[key] = product[key]
    } else if (key.startsWith('pim')) {
      product_pim[key] = product[key]
    }
  }

  const getBadges = (value: string) => {
    const logosArray = value.split(',')

    return logosArray.map((logo: string) => (
      <div key={logo} className="flex flex-col items-center space-y-4 p-3">
        <div className="w-16 h-16 sm:w-24  sm:h-24">
          <Image
            className="object-contain"
            src={`/assets/images/${logo
              .toLowerCase()
              .trim()
              .replace(/\s/g, '-')}.svg`}
            alt={logo}
            width={96}
            height={96}
          />
        </div>
        <div className="text-sm font-medium text-center text-gray-700">
          {logo}
        </div>
      </div>
    ))
  }

  return (
    <div className="w-full xl:px-2 pt-8 sm:px-0">
      <Tab.Group>
        <Tab.List className="block border-b border-border-base whitespace-nowrap overflow-x-auto overflow-y-hidden h-[36px] sm:h-[44px]">
          {Object.keys(tabHeading).map((item) => {
            if (
              (item === 'text-product-allergens' &&
                (!ProductTabData?.Allergens?.length ||
                  !Object.keys(ProductTabData?.Allergens[0]?.value || {})
                    .length)) ||
              (item === 'text-product-nutrition-facts' &&
                (!ProductTabData?.['Nutrition Facts']?.length ||
                  !Object.keys(
                    ProductTabData?.['Nutrition Facts'][0]?.value || {},
                  ).length)) ||
              (item === 'text-product-resources-and-downloads' &&
                !product?.media_files?.length)
            ) {
              return null
            }

            return (
              <Tab
                key={item}
                className={({ selected }) =>
                  classNames(
                    'relative inline-block transition-all lg:text-16px leading-5  focus:outline-none pb-3 lg:pb-5 hover:text-brand ltr:mr-8 rtl:ml-8',
                    selected
                      ? 'font-semibold after:absolute after:w-full after:h-0.5 after:bottom-0 after:translate-y-[1px] after:ltr:left-0 after:rtl:right-0 after:bg-brand'
                      : '',
                  )
                }
              >
                {translate(item)}
              </Tab>
            )
          })}
        </Tab.List>
        <Tab.Panels className="mt-6 lg:mt-9">
          <Tab.Panel className="">
            <div className="text-sm sm:text-15px text-brand-muted leading-[2em] space-y-4 lg:space-y-5 xl:space-y-7">
              {ProductTabData?.Content.map((content, index) => {
                switch (content.attribute_code) {
                  case 'title_2':
                    return (
                      <h2 key={index + '-' + content.attribute_code}>
                        <strong>{content.value}</strong>
                      </h2>
                    )
                  case 'short_description':
                    return (
                      <div
                        key={index + '-' + content.attribute_code}
                        dangerouslySetInnerHTML={{ __html: content.value }}
                      ></div>
                    )
                  case 'title_3':
                    return (
                      <h2
                        key={index + '-' + content.attribute_code}
                        className="pt-2"
                      >
                        <strong>{content.value}</strong>
                      </h2>
                    )
                  case 'featured_bullet':
                    return (
                      <div
                        key={index + '-' + content.attribute_code}
                        dangerouslySetInnerHTML={{ __html: content.value }}
                        className="pl-5 p-product-detail-featured-bullet"
                      ></div>
                    )
                  default:
                    return null
                }
              })}
              {product.description && (
                <p>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: product.description,
                    }}
                  />
                </p>
              )}

              {product?.ships_in_days === 1 && (
                <p>{translate('text-pdp-footer-msg')}</p>
              )}
              {ProductTabData.Fsd_Video && ProductTabData.Fsd_Video.length ? (
                <div id="product-video">
                  <div className="page__section-content">
                    <div className="relative pb-[50.25%] overflow-hidden">
                      <iframe
                        title="Product Video"
                        src={ProductTabData.Fsd_Video[0].value}
                        className="absolute top-0 left-0 w-full h-full border-0"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                </div>
              ) : null}
              {shouldShowFrozen && (
                <div className="flex items-start gap-4  lg:pr-[150px]">
                  <div className="relative h-16 w-16 flex-shrink-0">
                    <Image
                      src="/icons/ships-frozen-snow.svg"
                      alt={FROZEN}
                      fill
                      className="object-contain"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <div className="relative group text-brand-dark font-semibold text-lg">
                      <p>
                        {translate('text-ships', { ns: 'pdp' })}{' '}
                        {product?.temperature_description}{' '}
                        {translate('text-in-proprietary-boxes', { ns: 'pdp' })}
                      </p>
                    </div>

                    <p className="text-md text-brand-dark">
                      {product.temperature_description === FROZEN
                        ? translate('text-frozen-message', { ns: 'pdp' })
                        : translate('text-refrigerated-message', { ns: 'pdp' })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Tab.Panel>

          {ProductTabData?.[PRODUCT_TABS.PRODUCT_SPECIFICATIONS]?.length >
            0 && (
            <Tab.Panel>
              {ProductTabData?.[PRODUCT_TABS.PRODUCT_SPECIFICATIONS].map(
                ({ label, value }: { label: string; value: string }) =>
                  label === PRODUCT_TABS.FOOD_PROPERTIES ? (
                    <div
                      className="flex flex-wrap justify-center text-center"
                      key={label}
                    >
                      {getBadges(value)}
                    </div>
                  ) : (
                    <p
                      key={label}
                      className="flex  justify-between w-full md:w-[50%] mb-4"
                    >
                      <span className="text-brand-dark font-semibold">
                        {label}
                      </span>
                      <span>{value}</span>
                    </p>
                  ),
              )}
            </Tab.Panel>
          )}

          {ProductTabData?.Properties?.length > 0 && (
            <Tab.Panel className="w-full md:w-[50%]">
              {ProductTabData?.Properties.map(
                ({ label, value }: { label: string; value: string }) => (
                  <p key={label} className=" flex justify-between mb-4">
                    <span className="text-brand-dark font-semibold">
                      {label}
                    </span>
                    <span>{value}</span>
                  </p>
                ),
              )}
            </Tab.Panel>
          )}

          {ProductTabData?.Allergens?.length > 0 &&
            ProductTabData.Allergens[0]?.value &&
            Object.keys(ProductTabData.Allergens[0].value).length > 0 && (
              <Tab.Panel className="w-full md:w-[50%]">
                {Object.entries(ProductTabData.Allergens[0].value).map(
                  ([key, value]: [string, string]) => (
                    <p
                      key={key}
                      className="text-brand-dark font-semibold flex justify-between mb-4"
                    >
                      <span>{key}</span>{' '}
                      <span>
                        {value === PRODUCT_TABS.ALLERGENS.FREE_FROM
                          ? translate('text-no')
                          : translate('text-yes')}
                      </span>
                    </p>
                  ),
                )}
              </Tab.Panel>
            )}
          {ProductTabData?.[PRODUCT_TABS.NUTRITION_FACTS]?.length > 0 &&
            ProductTabData[PRODUCT_TABS.NUTRITION_FACTS][0]?.value &&
            Object.keys(ProductTabData[PRODUCT_TABS.NUTRITION_FACTS][0].value)
              .length > 0 && (
              <Tab.Panel className="w-full md:w-[50%]">
                <NutritionFacts
                  nutrition={Object.entries(
                    ProductTabData[PRODUCT_TABS.NUTRITION_FACTS][0]?.value,
                  )}
                />
              </Tab.Panel>
            )}
          {product?.media_files?.length && (
            <Tab.Panel className="w-full  md:w-[50%]">
              <ResourcesDownload mediaFiles={product?.media_files} />
            </Tab.Panel>
          )}
        </Tab.Panels>
      </Tab.Group>
    </div>
  )
}
